/**
 * Claims & Assertions Engine — Frontend
 * ═══════════════════════════════════════
 *
 * Route: /operations/claims
 * API:   GET  /api/v1/claims                    → All claims with status
 *        GET  /api/v1/claims/:id                → Single claim detail
 *        POST /api/v1/claims                    → Create claim from evidence
 *        PUT  /api/v1/claims/:id/status         → Approve/reject/draft
 *        GET  /api/v1/claims/:id/lineage        → Evidence → Claim → Disclosure chain
 *        GET  /api/v1/claims/pipeline/stats      → Pipeline metrics
 *        POST /api/v1/claims/generate            → AI-generate claim from datapoint
 *
 * Backend: claims_service.py (641 lines) — ACTIVE
 *
 * Pipeline: Evidence → Claim → Approval → Disclosure → Audit Pack
 * Every claim must trace back to at least one evidence document.
 * Every claim must have a named owner and approver.
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import {
  useFetch, useMutation, useGraphWS, useModuleNav,
  apiFetch, LoadingState, ErrorState, EmptyState, WSIndicator,
} from "../services/api_service";
import TracedValue from "../components/TracedValue";
import { JourneySection, JourneyPanel } from "./journeys/JourneyPanel";
import { AIClaimGeneration, ConsistencyCheck, RestatementWorkflow } from "./journeys/JourneyClaims";

const C = {
  bg: "#050a14", surface: "#0c1220", card: "#111c30", cardHover: "#162040",
  border: "#1a2744", borderHi: "#2a3a5c", text: "#e2e8f0", muted: "#6b7a96",
  dim: "#384660", blue: "#3b82f6", amber: "#f59e0b", emerald: "#10b981",
  purple: "#8b5cf6", cyan: "#06b6d4", red: "#ef4444", pink: "#ec4899",
  teal: "#14b8a6", orange: "#f97316", lime: "#84cc16", white: "#fff",
  indigo: "#6366f1",
};

const Pl = ({ color: cl, children, small, onClick, active, glow }) => (
  <span onClick={onClick} style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: small ? "1px 6px" : "3px 9px", borderRadius: 99, fontSize: small ? 8 : 10, fontWeight: 600, background: active ? cl + "22" : cl + "12", color: cl, border: `1px solid ${active ? cl + "66" : cl + "22"}`, cursor: onClick ? "pointer" : "default", transition: "all .2s", boxShadow: glow ? `0 0 6px ${cl}33` : "none" }}>{children}</span>
);
const Bar = ({ v, mx, cl, h = 5 }) => (
  <div style={{ width: "100%", height: h, borderRadius: 99, background: C.border, overflow: "hidden" }}>
    <div style={{ width: `${Math.min((v / mx) * 100, 100)}%`, height: "100%", borderRadius: 99, background: `linear-gradient(90deg,${cl}88,${cl})`, transition: "width .4s" }} />
  </div>
);

const STATUS_CONF = {
  draft:      { label: "Draft",    color: C.dim,     icon: "○", next: "in_review" },
  in_review:  { label: "In Review", color: C.amber,  icon: "◎", next: "approved" },
  approved:   { label: "Approved", color: C.emerald,  icon: "✓", next: "published" },
  published:  { label: "Published", color: C.cyan,    icon: "📤", next: null },
  rejected:   { label: "Rejected", color: C.red,      icon: "✕", next: "draft" },
};

const CLAIM_TYPES = [
  { id: "quantitative", label: "Quantitative", color: C.blue, desc: "Numeric assertion backed by measured data" },
  { id: "qualitative", label: "Qualitative", color: C.purple, desc: "Policy/practice assertion backed by documentation" },
  { id: "target", label: "Target/Commitment", color: C.teal, desc: "Forward-looking target with baseline evidence" },
  { id: "narrative", label: "Narrative", color: C.orange, desc: "Explanatory disclosure backed by multiple sources" },
];


export default function ClaimsPage() {
  const [view, setView] = useState("pipeline"); // pipeline | register | builder | lineage
  const [statusFilter, setStatusFilter] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [lineageClaim, setLineageClaim] = useState(null);
  const nav = useModuleNav();

  // ── Data fetching ──
  const { data: claimsData, loading: clLoading, error: clError, refetch: refetchClaims } = useFetch("/claims/list");
  const { data: pipelineStats, loading: plLoading, refetch: refetchPipeline } = useFetch("/claims/statistics");

  // WebSocket: refetch on cascade or claim events
  const { lastEvent, connected } = useGraphWS();
  useEffect(() => {
    if (lastEvent?.type === "claim.status.changed" || lastEvent?.type === "cascade.completed") {
      refetchClaims();
      refetchPipeline();
    }
  }, [lastEvent, refetchClaims, refetchPipeline]);

  const claims = claimsData?.claims || [];
  const stats = pipelineStats?.stats || {};

  const filteredClaims = useMemo(() => {
    let list = claims;
    if (statusFilter) list = list.filter(c => c.status === statusFilter);
    if (typeFilter) list = list.filter(c => c.claim_type === typeFilter);
    return list;
  }, [claims, statusFilter, typeFilter]);

  // ── Claim Builder State ──
  const [builderMode, setBuilderMode] = useState("manual"); // manual | ai
  const [newClaim, setNewClaim] = useState({ title: "", body: "", claim_type: "quantitative", framework: "", requirement_code: "", datapoint_id: "", evidence_ids: [] });
  const [aiGenerating, setAiGenerating] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  // ── Lineage State ──
  const [lineageData, setLineageData] = useState(null);
  const [lineageLoading, setLineageLoading] = useState(false);

  // ── Status Update ──
  const updateClaimStatus = async (claimId, newStatus) => {
    await apiFetch(`/claims/${claimId}/approve`, { method: "POST", body: { status: newStatus } });
    await refetchClaims();
    await refetchPipeline();
  };

  // ── Create Claim ──
  const createClaim = async () => {
    setCreateLoading(true);
    const result = await apiFetch("/claims", { method: "POST", body: newClaim });
    if (!result?._error) {
      setNewClaim({ title: "", body: "", claim_type: "quantitative", framework: "", requirement_code: "", datapoint_id: "", evidence_ids: [] });
      setView("register");
      await refetchClaims();
      await refetchPipeline();
    }
    setCreateLoading(false);
    return result;
  };

  // ── AI Generate Claim ──
  const aiGenerateClaim = async () => {
    if (!newClaim.datapoint_id) return;
    setAiGenerating(true);
    const result = await apiFetch("/claims/generate", {
      method: "POST", body: { datapoint_id: newClaim.datapoint_id, framework: newClaim.framework },
    });
    if (!result?._error && result?.claim) {
      setNewClaim(prev => ({
        ...prev,
        title: result.claim.title || prev.title,
        body: result.claim.body || prev.body,
        claim_type: result.claim.claim_type || prev.claim_type,
        evidence_ids: result.claim.evidence_ids || prev.evidence_ids,
      }));
    }
    setAiGenerating(false);
  };

  // ── Load Lineage ──
  const loadLineage = async (claimId) => {
    setLineageClaim(claimId);
    setLineageLoading(true);
    setView("lineage");
    const result = await apiFetch(`/claims/${claimId}/lineage`);
    if (!result?._error) setLineageData(result);
    setLineageLoading(false);
  };

  const views = [
    { id: "pipeline", l: "Pipeline Overview", i: "📊" },
    { id: "register", l: "Claims Register", i: "📋" },
    { id: "builder", l: "Claim Builder", i: "✏️" },
    { id: "lineage", l: "Evidence Lineage", i: "🔗" },
  ];


  // ═══ PIPELINE OVERVIEW ═══
  const PipelineView = () => {
    if (plLoading) return <LoadingState message="Loading pipeline metrics…" />;

    const draft = claims.filter(c => c.status === "draft").length;
    const review = claims.filter(c => c.status === "in_review").length;
    const approved = claims.filter(c => c.status === "approved").length;
    const published = claims.filter(c => c.status === "published").length;
    const rejected = claims.filter(c => c.status === "rejected").length;
    const total = claims.length;
    const pipelinePct = total > 0 ? Math.round((published / total) * 100) : 0;

    return (
      <div>
        {/* Pipeline Funnel */}
        <div style={{ padding: "14px 16px", borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.cyan, marginBottom: 8 }}>EVIDENCE → CLAIM → DISCLOSURE PIPELINE</div>
          <div style={{ display: "flex", gap: 4, alignItems: "stretch", marginBottom: 8 }}>
            {[
              { label: "Draft", count: draft, color: C.dim, pct: total > 0 ? (draft / total) * 100 : 0 },
              { label: "In Review", count: review, color: C.amber, pct: total > 0 ? (review / total) * 100 : 0 },
              { label: "Approved", count: approved, color: C.emerald, pct: total > 0 ? (approved / total) * 100 : 0 },
              { label: "Published", count: published, color: C.cyan, pct: total > 0 ? (published / total) * 100 : 0 },
            ].map((stage, i) => (
              <div key={i} onClick={() => { setStatusFilter(stage.label.toLowerCase().replace(" ", "_")); setView("register"); }}
                style={{ flex: Math.max(stage.pct, 10), padding: "12px 10px", borderRadius: 6, background: stage.color + "08", border: `1px solid ${stage.color}22`, cursor: "pointer", textAlign: "center", minWidth: 70, transition: "all .2s" }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: stage.color }}>{stage.count}</div>
                <div style={{ fontSize: 8, color: C.muted }}>{stage.label}</div>
                {i < 3 && <div style={{ position: "absolute", right: -6, top: "50%", transform: "translateY(-50%)", color: C.dim, fontSize: 10 }} />}
              </div>
            ))}
          </div>
          {rejected > 0 && (
            <div onClick={() => { setStatusFilter("rejected"); setView("register"); }}
              style={{ padding: "6px 10px", borderRadius: 5, background: C.red + "06", border: `1px solid ${C.red}18`, cursor: "pointer", fontSize: 9 }}>
              <span style={{ color: C.red, fontWeight: 700 }}>{rejected} rejected</span>
              <span style={{ color: C.muted }}> — need revision before resubmission</span>
            </div>
          )}
        </div>

        {/* KPIs */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 6, marginBottom: 10 }}>
          {[
            { l: "Total Claims", v: total, c: C.text },
            { l: "Published", v: published, c: C.cyan, sub: `${pipelinePct}% of pipeline` },
            { l: "Awaiting Review", v: review, c: C.amber },
            { l: "Evidence-Backed", v: stats.evidence_backed || claims.filter(c => c.evidence_count > 0).length, c: C.teal, sub: "with linked evidence" },
            { l: "Avg Quality Score", v: stats.avg_quality || "—", c: C.emerald },
            { l: "Frameworks Covered", v: stats.frameworks_covered || new Set(claims.map(c => c.framework).filter(Boolean)).size, c: C.purple },
          ].map((k, i) => (
            <div key={i} style={{ padding: "10px 12px", borderRadius: 7, background: C.card, border: `1px solid ${k.c}12` }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: k.c, lineHeight: 1 }}>{k.v}</div>
              <div style={{ fontSize: 9, fontWeight: 600, color: C.text, marginTop: 2 }}>{k.l}</div>
              {k.sub && <div style={{ fontSize: 7, color: C.dim }}>{k.sub}</div>}
            </div>
          ))}
        </div>

        {/* Recent Claims */}
        <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 6 }}>RECENT CLAIMS</div>
        {claims.slice(0, 8).map(claim => {
          const sc = STATUS_CONF[claim.status] || STATUS_CONF.draft;
          const tc = CLAIM_TYPES.find(t => t.id === claim.claim_type);
          return (
            <div key={claim.id || claim._id} onClick={() => { setSelectedClaim(claim.id || claim._id); setView("register"); }}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 6, background: C.card, border: `1px solid ${sc.color}12`, marginBottom: 3, cursor: "pointer", transition: "all .15s" }}>
              <Pl color={sc.color} small>{sc.icon} {sc.label}</Pl>
              {tc && <Pl color={tc.color} small>{tc.label}</Pl>}
              <div style={{ flex: 1, fontSize: 10, fontWeight: 600, color: C.text }}>{claim.title || "Untitled claim"}</div>
              <span style={{ fontSize: 8, color: C.muted }}>{claim.framework || "—"} {claim.requirement_code || ""}</span>
              <span style={{ fontSize: 8, color: claim.evidence_count > 0 ? C.teal : C.red }}>
                {claim.evidence_count || 0} evidence
              </span>
            </div>
          );
        })}
        {claims.length > 8 && (
          <div onClick={() => setView("register")} style={{ textAlign: "center", padding: "6px", fontSize: 9, color: C.blue, cursor: "pointer" }}>
            View all {claims.length} claims →
          </div>
        )}
      </div>
    );
  };


  // ═══ CLAIMS REGISTER ═══
  const RegisterView = () => {
    if (clLoading) return <LoadingState message="Loading claims register…" />;
    if (clError) return <ErrorState error={clError} onRetry={refetchClaims} />;
    if (!claims.length) return <EmptyState icon="📋" title="No claims yet" subtitle="Use the Claim Builder to create your first audit-defensible claim" />;

    return (
      <div>
        {/* Filters */}
        <div style={{ display: "flex", gap: 3, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 8, color: C.dim }}>STATUS:</span>
          <Pl color={C.text} small onClick={() => setStatusFilter(null)} active={!statusFilter}>All ({claims.length})</Pl>
          {Object.entries(STATUS_CONF).map(([key, conf]) => {
            const count = claims.filter(c => c.status === key).length;
            if (!count) return null;
            return <Pl key={key} color={conf.color} small onClick={() => setStatusFilter(statusFilter === key ? null : key)} active={statusFilter === key}>{conf.icon} {conf.label} ({count})</Pl>;
          })}
          <span style={{ fontSize: 8, color: C.dim, marginLeft: 8 }}>TYPE:</span>
          {CLAIM_TYPES.map(t => {
            const count = claims.filter(c => c.claim_type === t.id).length;
            if (!count) return null;
            return <Pl key={t.id} color={t.color} small onClick={() => setTypeFilter(typeFilter === t.id ? null : t.id)} active={typeFilter === t.id}>{t.label} ({count})</Pl>;
          })}
        </div>

        {/* Claims Table */}
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {filteredClaims.map(claim => {
            const sc = STATUS_CONF[claim.status] || STATUS_CONF.draft;
            const tc = CLAIM_TYPES.find(t => t.id === claim.claim_type);
            const isExpanded = selectedClaim === (claim.id || claim._id);

            return (
              <div key={claim.id || claim._id}>
                <div onClick={() => setSelectedClaim(isExpanded ? null : (claim.id || claim._id))}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: isExpanded ? "7px 7px 0 0" : 7, background: isExpanded ? sc.color + "06" : C.card, border: `1px solid ${isExpanded ? sc.color + "33" : sc.color + "12"}`, borderBottom: isExpanded ? "none" : undefined, cursor: "pointer" }}>
                  <Pl color={sc.color} small>{sc.icon} {sc.label}</Pl>
                  {tc && <Pl color={tc.color} small>{tc.label}</Pl>}
                  <div style={{ flex: 1, fontSize: 10, fontWeight: 600, color: C.text }}>{claim.title || "Untitled"}</div>
                  <Pl color={claim.framework ? C.purple : C.dim} small>{claim.framework || "No FW"} {claim.requirement_code || ""}</Pl>
                  <span style={{ fontSize: 8, color: claim.evidence_count > 0 ? C.teal : C.red }}>{claim.evidence_count || 0} evidence</span>
                  <span style={{ fontSize: 8, color: C.muted }}>{claim.owner || "Unassigned"}</span>
                  <span style={{ fontSize: 9, color: C.dim, transform: isExpanded ? "rotate(180deg)" : "rotate(0)", transition: "transform .2s" }}>▼</span>
                </div>

                {isExpanded && (
                  <div style={{ border: `1px solid ${sc.color}33`, borderTop: "none", borderRadius: "0 0 7px 7px", background: C.surface, padding: "12px 14px" }}>
                    {/* Claim Body */}
                    <div style={{ padding: "8px 10px", borderRadius: 5, background: C.card, fontSize: 10, color: C.text, lineHeight: 1.6, marginBottom: 8 }}>
                      {claim.body || claim.assertion_text || "No claim text"}
                    </div>

                    {/* Metadata Grid */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6, marginBottom: 8 }}>
                      <div style={{ padding: "5px 8px", borderRadius: 4, background: C.card }}>
                        <div style={{ fontSize: 7, color: C.muted }}>Datapoint</div>
                        {/* Phase 4: the claim's number traces back to its evidence chain */}
                        <TracedValue value={claim.datapoint_id || "—"} type="datapoint"
                          id={claim.datapoint_id} color={C.blue}
                          style={{ fontSize: 9, fontWeight: 600 }} />
                      </div>
                      <div style={{ padding: "5px 8px", borderRadius: 4, background: C.card }}>
                        <div style={{ fontSize: 7, color: C.muted }}>Owner</div>
                        <div style={{ fontSize: 9, fontWeight: 600, color: C.text }}>{claim.owner || "Unassigned"}</div>
                      </div>
                      <div style={{ padding: "5px 8px", borderRadius: 4, background: C.card }}>
                        <div style={{ fontSize: 7, color: C.muted }}>Approver</div>
                        <div style={{ fontSize: 9, fontWeight: 600, color: C.text }}>{claim.approver || "—"}</div>
                      </div>
                      <div style={{ padding: "5px 8px", borderRadius: 4, background: C.card }}>
                        <div style={{ fontSize: 7, color: C.muted }}>Quality</div>
                        <div style={{ fontSize: 9, fontWeight: 600, color: claim.quality_score >= 80 ? C.emerald : C.amber }}>{claim.quality_score || "—"}%</div>
                      </div>
                    </div>

                    {/* Evidence Links */}
                    {claim.evidence_ids?.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 8, fontWeight: 700, color: C.teal, marginBottom: 3 }}>LINKED EVIDENCE ({claim.evidence_ids.length})</div>
                        <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                          {claim.evidence_ids.map((eid, i) => (
                            <span key={i} onClick={() => nav.toEvidence(eid)} style={{ padding: "2px 6px", borderRadius: 3, background: C.teal + "12", color: C.teal, fontSize: 8, cursor: "pointer", border: `1px solid ${C.teal}22` }}>
                              📎 {eid}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      {sc.next && (
                        <button onClick={() => updateClaimStatus(claim.id || claim._id, sc.next)}
                          style={{ padding: "4px 12px", borderRadius: 4, border: `1px solid ${STATUS_CONF[sc.next].color}33`, background: STATUS_CONF[sc.next].color + "08", color: STATUS_CONF[sc.next].color, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>
                          → {STATUS_CONF[sc.next].label}
                        </button>
                      )}
                      {claim.status !== "rejected" && claim.status !== "draft" && (
                        <button onClick={() => updateClaimStatus(claim.id || claim._id, "rejected")}
                          style={{ padding: "4px 10px", borderRadius: 4, border: `1px solid ${C.red}22`, background: "transparent", color: C.red, fontSize: 9, cursor: "pointer" }}>
                          ✕ Reject
                        </button>
                      )}
                      <button onClick={() => loadLineage(claim.id || claim._id)}
                        style={{ padding: "4px 10px", borderRadius: 4, border: `1px solid ${C.indigo}22`, background: "transparent", color: C.indigo, fontSize: 9, cursor: "pointer" }}>
                        🔗 View Lineage
                      </button>
                      <div style={{ flex: 1 }} />
                      <span style={{ fontSize: 7, color: C.dim }}>Created: {claim.created_at || "—"} · Updated: {claim.updated_at || "—"}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };


  // ═══ CLAIM BUILDER ═══
  const BuilderView = () => (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.emerald }}>Create New Claim</div>
          <div style={{ fontSize: 9, color: C.muted }}>Every claim must be backed by evidence and linked to a framework requirement.</div>
        </div>
        <div style={{ display: "flex", gap: 2, background: C.surface, borderRadius: 5, padding: 2 }}>
          {[{ id: "manual", l: "Manual" }, { id: "ai", l: "AI Generate" }].map(m => (
            <button key={m.id} onClick={() => setBuilderMode(m.id)}
              style={{ padding: "4px 10px", borderRadius: 4, border: "none", background: builderMode === m.id ? C.card : "transparent", color: builderMode === m.id ? C.text : C.muted, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>
              {m.l}
            </button>
          ))}
        </div>
      </div>

      {builderMode === "ai" && (
        <div style={{ padding: "10px 14px", borderRadius: 8, background: C.purple + "06", border: `1px solid ${C.purple}22`, marginBottom: 10 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: C.purple, marginBottom: 4 }}>AI CLAIM GENERATION</div>
          <div style={{ fontSize: 8, color: C.muted, marginBottom: 8 }}>Select a datapoint and framework. AI will draft a disclosure claim with source citations. Human approval is mandatory before publication.</div>
          <div style={{ display: "flex", gap: 8, alignItems: "end" }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 8, color: C.muted }}>Datapoint ID</label>
              <input type="text" value={newClaim.datapoint_id} onChange={e => setNewClaim(p => ({ ...p, datapoint_id: e.target.value }))} placeholder="dp1"
                style={{ width: "100%", padding: "6px 8px", borderRadius: 4, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 10, boxSizing: "border-box" }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 8, color: C.muted }}>Target Framework</label>
              <select value={newClaim.framework} onChange={e => setNewClaim(p => ({ ...p, framework: e.target.value }))}
                style={{ width: "100%", padding: "6px 8px", borderRadius: 4, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 10 }}>
                <option value="">Select…</option>
                {["CSRD/ESRS", "GRI", "TCFD", "CDP", "SBTi", "SASB", "GHG Protocol"].map(f => <option key={f}>{f}</option>)}
              </select>
            </div>
            <button onClick={aiGenerateClaim} disabled={aiGenerating || !newClaim.datapoint_id}
              style={{ padding: "6px 16px", borderRadius: 4, border: "none", background: !newClaim.datapoint_id ? C.dim : C.purple, color: C.white, fontSize: 10, fontWeight: 700, cursor: !newClaim.datapoint_id ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}>
              {aiGenerating ? "Generating…" : "🧠 Generate Draft"}
            </button>
          </div>
        </div>
      )}

      {/* Claim Form */}
      <div style={{ padding: "14px 16px", borderRadius: 8, background: C.surface, border: `1px solid ${C.border}` }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <div>
            <label style={{ fontSize: 8, color: C.muted, marginBottom: 2, display: "block" }}>Claim Title</label>
            <input type="text" value={newClaim.title} onChange={e => setNewClaim(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Scope 1 GHG emissions for FY2024"
              style={{ width: "100%", padding: "8px 10px", borderRadius: 5, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 11, boxSizing: "border-box" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            <div>
              <label style={{ fontSize: 8, color: C.muted, marginBottom: 2, display: "block" }}>Framework</label>
              <select value={newClaim.framework} onChange={e => setNewClaim(p => ({ ...p, framework: e.target.value }))}
                style={{ width: "100%", padding: "8px 10px", borderRadius: 5, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 10 }}>
                <option value="">Select…</option>
                {["CSRD/ESRS", "GRI", "TCFD", "CDP", "SBTi", "SASB", "GHG Protocol"].map(f => <option key={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 8, color: C.muted, marginBottom: 2, display: "block" }}>Requirement Code</label>
              <input type="text" value={newClaim.requirement_code} onChange={e => setNewClaim(p => ({ ...p, requirement_code: e.target.value }))} placeholder="e.g. E1-6, 305-1"
                style={{ width: "100%", padding: "8px 10px", borderRadius: 5, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 10, boxSizing: "border-box" }} />
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 8, color: C.muted, marginBottom: 2, display: "block" }}>Claim Body (the assertion text that will appear in the disclosure)</label>
          <textarea value={newClaim.body} onChange={e => setNewClaim(p => ({ ...p, body: e.target.value }))} rows={4} placeholder="e.g. In FY2024, the Company's direct (Scope 1) GHG emissions were 42,850 tCO₂e, calculated using the GHG Protocol Corporate Standard…"
            style={{ width: "100%", padding: "10px 12px", borderRadius: 5, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 10, lineHeight: 1.6, resize: "vertical", boxSizing: "border-box" }} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
          <div>
            <label style={{ fontSize: 8, color: C.muted, marginBottom: 2, display: "block" }}>Claim Type</label>
            <select value={newClaim.claim_type} onChange={e => setNewClaim(p => ({ ...p, claim_type: e.target.value }))}
              style={{ width: "100%", padding: "8px 10px", borderRadius: 5, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 10 }}>
              {CLAIM_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 8, color: C.muted, marginBottom: 2, display: "block" }}>Linked Datapoint</label>
            <input type="text" value={newClaim.datapoint_id} onChange={e => setNewClaim(p => ({ ...p, datapoint_id: e.target.value }))} placeholder="dp1"
              style={{ width: "100%", padding: "8px 10px", borderRadius: 5, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 10, boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ fontSize: 8, color: C.muted, marginBottom: 2, display: "block" }}>Evidence IDs (comma-separated)</label>
            <input type="text" value={newClaim.evidence_ids.join(", ")} onChange={e => setNewClaim(p => ({ ...p, evidence_ids: e.target.value.split(",").map(s => s.trim()).filter(Boolean) }))} placeholder="ev1, ev2, ev3"
              style={{ width: "100%", padding: "8px 10px", borderRadius: 5, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 10, boxSizing: "border-box" }} />
          </div>
        </div>

        <button onClick={createClaim} disabled={createLoading || !newClaim.title || !newClaim.body}
          style={{ padding: "8px 24px", borderRadius: 5, border: "none", background: (!newClaim.title || !newClaim.body) ? C.dim : C.emerald, color: C.white, fontSize: 11, fontWeight: 700, cursor: (!newClaim.title || !newClaim.body) ? "not-allowed" : "pointer" }}>
          {createLoading ? "Creating…" : "✓ Create Claim (as Draft)"}
        </button>
      </div>
    </div>
  );


  // ═══ EVIDENCE LINEAGE ═══
  const LineageView = () => {
    if (!lineageClaim) return <EmptyState icon="🔗" title="Select a claim to view lineage" subtitle="Click 'View Lineage' on any claim in the register" />;
    if (lineageLoading) return <LoadingState message="Tracing evidence chain…" />;
    if (!lineageData) return <ErrorState error={{ message: "Lineage data unavailable" }} />;

    const chain = lineageData.chain || [];
    const claim = lineageData.claim || {};

    return (
      <div>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.indigo, marginBottom: 4 }}>Evidence Lineage: {claim.title || lineageClaim}</div>
        <div style={{ fontSize: 9, color: C.muted, marginBottom: 12 }}>
          Complete audit chain from raw evidence → datapoint → claim → disclosure. Every link is immutable and timestamped.
        </div>

        {/* Lineage Chain */}
        <div style={{ position: "relative", paddingLeft: 20 }}>
          <div style={{ position: "absolute", left: 9, top: 14, bottom: 14, width: 2, background: C.indigo + "33", borderRadius: 99 }} />

          {chain.map((node, i) => {
            const nodeColors = { evidence: C.teal, datapoint: C.blue, claim: C.emerald, disclosure: C.cyan, audit: C.indigo };
            const nc = nodeColors[node.type] || C.dim;
            return (
              <div key={i} style={{ position: "relative", marginBottom: 6, paddingLeft: 16 }}>
                <div style={{ position: "absolute", left: -11, top: 10, width: 10, height: 10, borderRadius: "50%", background: nc + "22", border: `2px solid ${nc}`, zIndex: 1 }} />
                <div style={{ padding: "10px 12px", borderRadius: 6, background: C.card, border: `1px solid ${nc}33` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                    <Pl color={nc} small>{node.type}</Pl>
                    <span style={{ fontSize: 10, fontWeight: 600, color: C.text }}>{node.title || node.name || node.id}</span>
                    <span style={{ marginLeft: "auto", fontSize: 7, color: C.dim, fontFamily: "monospace" }}>{node.timestamp || "—"}</span>
                  </div>
                  {node.description && <div style={{ fontSize: 8, color: C.muted, marginBottom: 3 }}>{node.description}</div>}
                  {node.hash && <div style={{ fontSize: 7, color: C.dim, fontFamily: "monospace" }}>SHA-256: {node.hash.slice(0, 16)}…</div>}
                  {node.actor && <div style={{ fontSize: 7, color: C.indigo }}>Actor: {node.actor}</div>}
                  {node.id && node.type === "evidence" && (
                    <span onClick={() => nav.toEvidence(node.id)} style={{ fontSize: 8, color: C.teal, cursor: "pointer" }}>View evidence →</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {chain.length === 0 && (
          <div style={{ textAlign: "center", padding: 20, color: C.dim }}>
            <div style={{ fontSize: 9 }}>No lineage chain available. The claim may not have linked evidence yet.</div>
          </div>
        )}

        {/* Defensibility Score */}
        {lineageData.defensibility_score != null && (
          <div style={{ marginTop: 10, padding: "10px 14px", borderRadius: 8, background: C.emerald + "06", border: `1px solid ${C.emerald}18` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: C.emerald }}>DEFENSIBILITY SCORE</div>
              <div style={{ flex: 1 }}><Bar v={lineageData.defensibility_score} mx={100} cl={lineageData.defensibility_score >= 80 ? C.emerald : C.amber} h={5} /></div>
              <span style={{ fontSize: 14, fontWeight: 800, color: lineageData.defensibility_score >= 80 ? C.emerald : C.amber }}>{lineageData.defensibility_score}%</span>
            </div>
            <div style={{ fontSize: 8, color: C.muted, marginTop: 4 }}>
              {lineageData.defensibility_score >= 80 ? "Strong: Complete evidence chain with named actors and timestamps." : "Needs improvement: Missing evidence links or approval gaps."}
            </div>
          </div>
        )}
      </div>
    );
  };


  // ═══ RENDER ═══
  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <div style={{ padding: "8px 16px 5px", borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, zIndex: 10, background: C.bg + "f0", backdropFilter: "blur(12px)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
          <div style={{ width: 24, height: 24, borderRadius: 5, background: `linear-gradient(135deg, ${C.emerald}, ${C.cyan})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, color: C.white }}>C</div>
          <span style={{ fontSize: 12, fontWeight: 700 }}>DisclosureOS</span>
          <span style={{ color: C.dim }}>/</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: C.purple }}>Operations</span>
          <span style={{ color: C.dim }}>/</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: C.emerald }}>Claims & Assertions</span>
          <div style={{ flex: 1 }} />
          <WSIndicator connected={connected} />
          <Pl color={C.emerald} small>{claims.filter(c => c.status === "published").length} published</Pl>
          <Pl color={C.amber} small>{claims.filter(c => c.status === "in_review").length} reviewing</Pl>
        </div>
        <div style={{ display: "flex", gap: 3 }}>
          {views.map(v => (
            <button key={v.id} onClick={() => setView(v.id)} style={{
              display: "flex", alignItems: "center", gap: 3, padding: "4px 9px", borderRadius: 5,
              fontSize: 9, fontWeight: 600, cursor: "pointer",
              border: `1px solid ${view === v.id ? C.emerald : C.border}`,
              background: view === v.id ? C.emerald + "15" : "transparent",
              color: view === v.id ? C.emerald : C.muted,
            }}>{v.i} {v.l}</button>
          ))}
        </div>
      </div>

      <div style={{ padding: "12px 16px 30px", maxWidth: 1200, margin: "0 auto" }}>
        {view === "pipeline" && <PipelineView />}
        {view === "register" && <RegisterView />}
        {view === "builder" && <BuilderView />}
        {view === "lineage" && <LineageView />}

        {/* Live User Journeys */}
        <JourneySection title="Live Journeys">
          <JourneyPanel title="AI Claim Generation" ucId="UC-08"><AIClaimGeneration /></JourneyPanel>
          <JourneyPanel title="Cross-Framework Consistency Check" ucId="UC-24"><ConsistencyCheck /></JourneyPanel>
          <JourneyPanel title="Restatement Workflow" ucId="UC-26"><RestatementWorkflow /></JourneyPanel>
        </JourneySection>
      </div>

      <div style={{ textAlign: "center", padding: "8px 16px 14px", borderTop: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 10, fontWeight: 300, color: C.muted, fontStyle: "italic" }}>Evidence → Claim → Disclosure. <span style={{ color: C.emerald, fontWeight: 600 }}>Every assertion audit-defensible.</span></div>
      </div>
    </div>
  );
}
